import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';

function TaskList({ refreshTrigger, onTaskStatusChange }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isShiftActive, setIsShiftActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(25);
  const [animKey, setAnimKey] = useState(0);

  const [banInfo, setBanInfo] = useState(null);
  const [userStats, setUserStats] = useState({ rating: 25, consecutiveSkips: 0 });
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  
  const [timeRemaining, setTimeRemaining] = useState('00:00:00');

  const [seenTaskIds, setSeenTaskIds] = useState([]);
  const seenTaskIdsRef = useRef([]);
  const timerRef = useRef(null);

  const formatTimeRemaining = (date) => {
    const now = new Date();
    const diff = date - now;
    
    if (diff <= 0) return '00:00:00';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!banInfo) return;

    const timer = setInterval(() => {
      const remaining = formatTimeRemaining(banInfo.bannedUntil);
      setTimeRemaining(remaining);
      
      if (remaining === '00:00:00') {
        api.get('tasks/random/')
          .then(() => {
            setBanInfo(null);
          })
          .catch(() => {
            // Все еще забанен, продолжаем отсчет
          });
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [banInfo]);

  const getCategoryBadge = (categoryCode) => {
    const categories = {
      'AI': { label: '🤖 ИИ и Нейросети', bg: 'bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-200' },
      'MICRO': { label: '⚡ Микро-задача', bg: 'bg-amber-500/10 text-amber-600 border-amber-200' },
      'TG_SMM': { label: '📱 Telegram / SMM', bg: 'bg-sky-500/10 text-sky-600 border-sky-200' },
      'COPYWRITE': { label: '✍️ Копирайтинг', bg: 'bg-emerald-500/10 text-emerald-600 border-emerald-200' },
      'DESIGN': { label: '🎨 Дизайн', bg: 'bg-pink-500/10 text-pink-600 border-pink-200' },
      'VIDEO': { label: '🎬 Видеомонтаж', bg: 'bg-rose-500/10 text-rose-600 border-rose-200' },
      'CRYPTO': { label: '🪙 Крипта / Web3', bg: 'bg-violet-500/10 text-violet-600 border-violet-200' },
      'DEV': { label: '💻 Разработка', bg: 'bg-blue-500/10 text-blue-600 border-blue-200' },
      'OTHER': { label: '📌 Другое', bg: 'bg-slate-500/10 text-slate-600 border-slate-200' },
    };

    return categories[categoryCode] || { label: '⚡ Задание', bg: 'bg-indigo-500/10 text-indigo-600 border-indigo-200' };
  };

  const fetchRandomTask = useCallback(() => {
    setLoading(true);
    setTimeLeft(25);

    const currentSeen = seenTaskIdsRef.current;
    const excludeParam = currentSeen.length > 0 
      ? `?exclude=${currentSeen.join(',')}` 
      : '';

    api.get(`tasks/random/${excludeParam}`)
      .then(res => {
        if (res.data.user_rating !== undefined) {
          setUserStats(prev => ({
            ...prev,
            rating: res.data.user_rating,
            consecutiveSkips: res.data.consecutive_skips || 0
          }));
        }

        if (!res.data || res.data.detail || !res.data.id) {
          setTask(null);
        } else {
          const newTask = res.data;
          if (currentSeen.includes(newTask.id)) {
            setTask(null);
          } else {
            setTask(newTask);
            setAnimKey(prev => prev + 1);
          }
        }
      })
      .catch((err) => {
        if (err.response?.status === 403 && err.response?.data?.banned_until) {
          setBanInfo({
            bannedUntil: new Date(err.response.data.banned_until),
            reason: err.response.data.error || 'Вы временно заблокированы'
          });
        } else {
          console.error("Ошибка запроса новой задачи:", err);
          setTask(null);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (refreshTrigger > 0 && isShiftActive) {
      fetchRandomTask();
    }
  }, [refreshTrigger, isShiftActive, fetchRandomTask]);

  const handleSkip = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!task) return;

    if (!seenTaskIdsRef.current.includes(task.id)) {
      const nextSeen = [...seenTaskIdsRef.current, task.id];
      seenTaskIdsRef.current = nextSeen;
      setSeenTaskIds(nextSeen);
    }

    try {
      const response = await api.post('tasks/skip/');
      
      if (response.data?.warning) {
        setWarningMessage(response.data.warning);
        setShowWarning(true);
      }
      
      setUserStats(prev => ({
        ...prev,
        consecutiveSkips: (prev.consecutiveSkips || 0) + 1
      }));
      
    } catch (err) {
      console.error("Ошибка при пропуске:", err);
      
      if (err.response?.status === 403 && err.response?.data?.banned_until) {
        setBanInfo({
          bannedUntil: new Date(err.response.data.banned_until),
          reason: err.response.data.error || 'Вы временно заблокированы'
        });
      }
    } finally {
      fetchRandomTask();
    }
  };

  const handlePause = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTask(null);
    setIsShiftActive(false);
  };

  const handleResetSearch = () => {
    seenTaskIdsRef.current = [];
    setSeenTaskIds([]);
    setIsShiftActive(true);
    fetchRandomTask();
  };

  useEffect(() => {
    if (isShiftActive && task && !loading) {
      setTimeLeft(25);
      
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSkip();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [task, isShiftActive, loading]);

  const handleStartShift = () => {
    setIsShiftActive(true);
    fetchRandomTask();
  };

  const handleAccept = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!task) return;

    if (!seenTaskIdsRef.current.includes(task.id)) {
      const nextSeen = [...seenTaskIdsRef.current, task.id];
      seenTaskIdsRef.current = nextSeen;
      setSeenTaskIds(nextSeen);
    }

    try {
      const res = await api.get('tasks/my-active/');
      if (res.data.length >= 3) {
        alert("Лимит достигнут! У вас уже есть 3 активные задачи.");
        fetchRandomTask();
        return;
      }
      
      await api.patch(`tasks/${task.id}/`, { status: 'in_progress', is_active: false });
      alert("Задача успешно взята!");
      
      if (onTaskStatusChange) {
        onTaskStatusChange();
      } else {
        fetchRandomTask();
      }
    } catch (err) {
      alert("Не удалось взять заказ.");
      fetchRandomTask();
    }
  };

  // 0. ЭКРАН: Пользователь заблокирован
  if (banInfo) {
    return (
      <div className="w-full max-w-xl mx-auto mt-4 px-4 font-sans text-slate-900">
        <Helmet>
          <title>Доступ ограничен — RealWork.pro</title>
        </Helmet>

        <div className="relative overflow-hidden bg-white/90 backdrop-blur-xl border-2 border-rose-200/80 rounded-[36px] p-8 text-center shadow-[0_20px_60px_-15px_rgba(244,63,94,0.15)]">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative flex justify-center mb-6">
            <div className="relative flex items-center justify-center w-24 h-24 bg-gradient-to-tr from-rose-50 to-white rounded-3xl border-2 border-rose-200/60 shadow-lg">
              <span className="text-5xl">🚫</span>
            </div>
          </div>

          <div className="max-w-md mx-auto mb-8">
            <h1 className="text-3xl font-black text-rose-600 tracking-tight leading-tight mb-3">
              Доступ ограничен
            </h1>
            <p className="text-base text-slate-600 font-medium leading-relaxed mb-4">
              {banInfo.reason}
            </p>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 mb-4">
              <p className="text-xs text-rose-700 font-bold uppercase tracking-wider mb-2">До разблокировки:</p>
              <div className="text-4xl font-black text-rose-600 font-mono">
                {timeRemaining}
              </div>
            </div>
            <p className="text-xs text-slate-400 font-medium">
              После окончания таймера доступ к ленте автоматически восстановится
            </p>
          </div>

          <div className="relative inline-block w-full max-w-sm group">
            <button
              onClick={() => {
                setBanInfo(null);
                setIsShiftActive(false);
              }}
              className="relative w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-4 px-8 rounded-2xl transition-all duration-300 flex items-center justify-center gap-2 text-sm cursor-pointer"
            >
              <span>Вернуться на главную</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 1. ЭКРАН: Режим ожидания
  if (!isShiftActive) {
    return (
      <div className="w-full max-w-xl mx-auto mt-4 px-4 font-sans text-slate-900">
        <Helmet>
          <title>Поиск заказов и работы — RealWork.pro</title>
          <meta name="description" content="Лента фриланс-задач и мгновенных подработок." />
        </Helmet>

        <div className="relative overflow-hidden bg-white/90 backdrop-blur-xl border border-slate-200/80 rounded-[36px] p-8 text-center shadow-[0_20px_60px_-15px_rgba(15,23,42,0.08)]">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative flex justify-center mb-6">
            <div className="relative flex items-center justify-center w-24 h-24 bg-gradient-to-tr from-slate-50 to-white rounded-3xl border border-slate-200/60 shadow-lg">
              <span className="text-5xl transform -rotate-12 animate-bounce [animation-duration:3s]">🚀</span>
              <span className="absolute -top-2 -right-2 text-2xl filter drop-shadow">⚡</span>
            </div>
          </div>

          <div className="max-w-md mx-auto mb-8">
            <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight mb-3">
              Готовы к новым <br />
              <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-500 bg-clip-text text-transparent">
                заказам?
              </span>
            </h1>
            <p className="text-sm text-slate-500 font-medium leading-relaxed px-2">
              После старта система выдаст случайный заказ. У вас будет <span className="font-bold text-slate-700">25 секунд</span> на выбор, иначе — автопропуск и <span className="text-rose-500 font-bold">-2 балла</span> рейтинга.
            </p>
          </div>

          <div className="relative inline-block w-full max-w-sm group">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition-opacity duration-300 pointer-events-none" />
            
            <button
              onClick={handleStartShift}
              className="relative w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-black py-4 px-8 rounded-2xl shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 tracking-wide text-base cursor-pointer"
            >
              <span>Приступить к поиску</span>
              <svg className="w-5 h-5 transition-transform duration-300 transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-6 text-xs text-slate-400 font-semibold">
            <span className="flex items-center gap-1.5">🛡️ Безопасная сделка</span>
            <span className="w-1.5 h-1.5 bg-slate-200 rounded-full" />
            <span className="flex items-center gap-1.5">💸 Мгновенный вывод</span>
          </div>
        </div>
      </div>
    );
  }

  // 2. ЭКРАН: Загрузка
  if (loading) return (
    <div className="w-full max-w-xl mx-auto p-12 text-center mt-6 bg-white/80 rounded-3xl border border-slate-100 shadow-sm animate-pulse">
      <Helmet>
        <title>Поиск заказов... — RealWork.pro</title>
      </Helmet>
      <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <span className="text-sm font-bold text-slate-500">Подбираем подходящее задание...</span>
    </div>
  );

  // 3. ЭКРАН: Предупреждение
  if (showWarning) {
    return (
      <div className="w-full max-w-xl mx-auto mt-4 px-4 font-sans text-slate-900">
        <div className="relative overflow-hidden bg-white/90 backdrop-blur-xl border-2 border-amber-300 rounded-[36px] p-8 text-center shadow-[0_20px_60px_-15px_rgba(245,158,11,0.2)]">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative flex justify-center mb-6">
            <div className="flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full border-4 border-amber-300 shadow-lg animate-pulse">
              <span className="text-4xl">⚠️</span>
            </div>
          </div>

          <h2 className="text-2xl font-black text-amber-700 mb-4">
            Внимание!
          </h2>
          
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
            <p className="text-sm text-amber-800 font-bold leading-relaxed">
              {warningMessage}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setShowWarning(false);
                fetchRandomTask();
              }}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-black py-4 px-8 rounded-2xl shadow-lg transition-all duration-300 cursor-pointer"
            >
              Понял, продолжу поиск
            </button>
            
            <button
              onClick={() => {
                setShowWarning(false);
                setIsShiftActive(false);
              }}
              className="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold py-3 px-6 rounded-2xl transition cursor-pointer text-sm"
            >
              Сделать перерыв
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 4. ЭКРАН: Карточка задачи
  return (
    <div className="w-full max-w-xl mx-auto p-0 mt-4 px-4 font-sans"> 
      {task && (
        <Helmet>
          <title>{`${task.title} — Заказ №${task.id} на RealWork.pro`}</title>
        </Helmet>
      )}

      {task ? (
        // 🆕 ОПРЕДЕЛЯЕМ, ЯВЛЯЕТСЯ ЛИ ЗАДАЧА "ПРЕМИУМ" (цена > 2.00)
        (() => {
          const isPremium = parseFloat(task.price) > 2.00;

          return (
            <div 
              key={animKey}
              className={`rounded-[32px] p-6 sm:p-8 relative overflow-hidden transition-all duration-300 transform animate-card-slide-in border ${
                isPremium 
                  ? 'bg-gradient-to-br from-purple-50/60 to-white border-2 border-purple-400/60 shadow-[0_15px_45px_-10px_rgba(168,85,247,0.2)]' 
                  : 'bg-white border border-slate-200/80 shadow-[0_15px_45px_-10px_rgba(15,23,42,0.06)]'
              }`}
            >
              {/* 🆕 ПОЛОСКА ТАЙМЕРА (меняет цвет для премиум) */}
              <div className={`absolute top-0 left-0 right-0 h-1.5 ${isPremium ? 'bg-purple-100' : 'bg-slate-100'}`}>
                <div 
                  className={`h-full transition-all duration-1000 ease-linear ${
                    timeLeft <= 5 
                      ? 'bg-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.8)]' 
                      : isPremium 
                        ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500' 
                        : 'bg-gradient-to-r from-indigo-500 to-blue-500'
                  }`}
                  style={{ width: `${(timeLeft / 25) * 100}%` }}
                />
              </div>

              <div className="flex justify-between items-center gap-2 pt-1">
                 <div className="flex items-center gap-2 overflow-hidden flex-wrap">
                   {(() => {
                     const badge = getCategoryBadge(task.category);
                     return (
                       <span className={`text-[11px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-xl border ${badge.bg}`}>
                         {badge.label}
                       </span>
                     );
                   })()}

                   <span className="text-[11px] font-mono font-bold text-slate-500 bg-slate-100 border border-slate-200/80 px-2.5 py-1.5 rounded-xl">
                     #{task.id}
                   </span>

                   {/* 🆕 БЕЙДЖ ПРЕМИУМ ЗАДАЧИ */}
                   {isPremium && (
                     <span className="text-[11px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-xl border bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 text-purple-700 border-purple-300 shadow-sm flex items-center gap-1 animate-pulse">
                       ⚡ Повышенный кэш
                     </span>
                   )}

                   {/* БЕЙДЖ ПРИГЛАШЕНИЯ (БЕЛЫЙ СПИСОК) */}
                   {task.is_whitelisted_for_me && (
                     <span className="text-[11px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-xl border bg-gradient-to-r from-amber-400/20 to-orange-400/20 text-amber-700 border-amber-300 shadow-sm animate-pulse flex items-center gap-1">
                       ⭐ Приглашение
                     </span>
                   )}
                   
                   <span className={`text-[11px] font-bold px-2.5 py-1.5 rounded-xl border ${
                     userStats.rating < 0 
                       ? 'bg-rose-100 text-rose-700 border-rose-200' 
                       : userStats.rating < 100 
                         ? 'bg-amber-100 text-amber-700 border-amber-200'
                         : 'bg-emerald-100 text-emerald-700 border-emerald-200'
                   }`}>
                     ⭐ {userStats.rating}
                   </span>
                 </div>

                 <div className={`flex items-center gap-1.5 font-mono text-xs font-black px-3 py-1.5 rounded-xl border transition-colors shrink-0 ${
                   timeLeft <= 5 
                     ? 'bg-rose-500 text-white border-rose-600 animate-pulse shadow-md shadow-rose-500/20' 
                     : 'bg-slate-900 text-white border-slate-900'
                 }`}>
                   <span>⏱️</span>
                   <span>00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}</span>
                 </div>
              </div>
              
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-5 tracking-tight leading-snug">
                {task.title}
              </h2>
              
              <div className="mt-4 p-4 sm:p-5 bg-slate-50/80 rounded-2xl border border-slate-200/50 max-h-60 overflow-y-auto">
                <p className="whitespace-pre-line text-slate-700 text-sm leading-relaxed font-medium">
                  {task.description}
                </p>
              </div>

              {/* 🆕 БЛОК ЦЕНЫ (меняет стиль для премиум) */}
              <div className={`mt-5 p-4 rounded-2xl flex items-center justify-between border ${
                isPremium 
                  ? 'bg-gradient-to-br from-purple-100/80 via-fuchsia-50/60 to-white border-purple-200/80' 
                  : 'bg-gradient-to-br from-indigo-50/70 via-blue-50/40 to-slate-50 border-indigo-100/80'
              }`}>
                <div>
                  <p className={`text-[10px] uppercase font-black tracking-widest mb-0.5 ${isPremium ? 'text-purple-900/70' : 'text-indigo-900/60'}`}>
                    Вознаграждение
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-3xl font-black tracking-tight ${isPremium ? 'text-purple-700' : 'text-slate-900'}`}>
                      {task.price} <span className={`text-2xl ${isPremium ? 'text-fuchsia-600' : 'text-indigo-600'}`}>USDT</span>
                    </span>
                    <span className="text-xs text-slate-400 font-bold">≈ {(Number(task.price) * 90).toFixed(0)} ₽</span>
                  </div>
                </div>
                
                <div className="text-right">
                  <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-lg shadow-sm border ${
                    isPremium ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-indigo-100 text-indigo-700'
                  }`}>
                    Комиссия 7%
                  </span>
                </div>
              </div>
              
              <div className="mt-6 flex flex-col gap-2.5">
                <div className="flex gap-3">
                  <button 
                    onClick={handleSkip}
                    className="flex-1 px-5 py-4 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-2xl font-black text-sm transition active:scale-[0.98] cursor-pointer"
                  >
                    Пропустить
                  </button>
                  <button 
                    onClick={handleAccept} 
                    className="flex-[1.8] px-5 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-2xl font-black text-sm shadow-lg shadow-emerald-500/25 transition transform active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>Взять заказ</span>
                    <span>➔</span>
                  </button>
                </div>

                <button 
                  onClick={handlePause}
                  className="w-full py-3 px-4 bg-slate-100/80 hover:bg-slate-200/80 border border-slate-200/80 text-slate-700 hover:text-slate-900 rounded-2xl transition-all text-xs font-bold flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
                >
                  <span>⏸️</span>
                  <span>Поставить поиск на паузу</span>
                </button>
              </div>

            </div>
          );
        })()
      ) : (
        <div className="text-center p-10 bg-white rounded-[32px] border border-slate-200/80 shadow-sm">
          <Helmet>
            <title>Задачи не найдены — RealWork.pro</title>
          </Helmet>
          <div className="text-4xl mb-3">☕</div>
          <h3 className="text-lg font-black text-slate-900 mb-1">Все доступные заказы просмотрены</h3>
          <p className="text-slate-500 mb-6 text-xs font-medium">Вы можете проверить список снова без перезагрузки страницы.</p>
          <div className="flex flex-col gap-2.5 max-w-xs mx-auto">
            <button 
              onClick={handleResetSearch} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-2xl shadow-md transition text-xs active:scale-[0.98] cursor-pointer"
            >
              🔄 Проверить снова
            </button>
            <button 
              onClick={handlePause} 
              className="text-slate-400 hover:text-slate-600 transition text-xs font-bold py-2 cursor-pointer"
            >
              Вернуться на главный экран
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskList;